
// A response the backend SERVED and this client cannot read (round «errori
// onesti», 2026-08-03).
//
// The generated zod validates every response against the contract this client
// was built from. When a backend is newer than its client — a driver, a phase,
// a problem code added by a later round — the HTTP call SUCCEEDED and the value
// is legitimate: it is the client that does not know it. Before this class the
// failure travelled as a bare `ZodError`, indistinguishable at the call site
// from `fetch` refusing to connect, so every consumer collapsed it into "is the
// backend reachable?" — a message that accuses the wrong party and sends the
// operator to look at a service that is answering `200` (bug hunt #157, finding
// U1: one `nvme-tcp` storage killed the whole Home dashboard, forever).
//
// The error therefore NAMES the offending field and value: that is the only
// thing that makes the incident diagnosable without a debugger.

import type { z } from "zod";



export interface ContractIssue {
    field: string;
    value: string;
    message: string;
}



function renderValue(value: unknown): string {
    if (value === undefined) {
        return "(missing)";
    }
    // JSON.stringify rende `undefined` su funzioni e symbol: quel caso non
    // esiste in una risposta JSON, ma il messaggio non deve mai diventare vuoto.
    const rendered = JSON.stringify(value) ?? "(non rappresentabile)";
    return rendered.length > 120 ? `${rendered.slice(0, 117)}…` : rendered;
}



function valueAt(data: unknown, path: readonly PropertyKey[]): unknown {
    let cursor: unknown = data;
    for (const step of path) {
        if (cursor === null || typeof cursor !== "object") {
            return undefined;
        }
        cursor = (cursor as Record<PropertyKey, unknown>)[step];
    }
    return cursor;
}



export class TVirtResponseError extends Error {

    // Every field the response violates, in the order zod reported them.
    readonly issues: ContractIssue[];
    // The first offending field and value — what a one-line message shows.
    readonly field: string;
    readonly value: string;
    // The request whose response failed, for the log line.
    readonly url: string;



    constructor(url: string, error: z.ZodError, data: unknown, prefix = "") {
        const issues: ContractIssue[] = error.issues.map((issue) => ({
            field: [...(prefix === "" ? [] : [prefix]), ...issue.path.map((step) => String(step))].join("."),
            value: renderValue(valueAt(data, issue.path)),
            message: issue.message,
        }));
        const first = issues[0];
        super(
            first === undefined
                ? `the backend response does not match the contract this client knows (${url})`
                : `the backend response contains a value this client does not know: ${first.field}=${first.value} (${url})`,
        );
        this.name = "TVirtResponseError";
        this.issues = issues;
        this.field = first?.field ?? "";
        this.value = first?.value ?? "";
        this.url = url;
    }

}
